import BackupVaultPage from '@/pages/BackupVaultPage'
import { useEffect, Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/lib/auth'
import Layout from '@/components/Layout'
import LoadingSpinner from '@/components/LoadingSpinner'
import DevDiagnostics from '@/components/DevDiagnostics'
import ForcePasswordChangeModal from '@/components/ForcePasswordChangeModal'
import RequirePaymentMethodGate from '@/components/RequirePaymentMethodGate'
import RequireEmailVerificationGate from '@/components/RequireEmailVerificationGate'
import RegistrationSessionStartupCoordinator from '@/components/RegistrationSessionStartupCoordinator'
import ServerCancellationProvider from '@/components/server/ServerCancellationProvider'
import { isAuthShellPath } from '@/lib/authRoutes'
import { SignalSlashIcon } from '@heroicons/react/24/outline'

// Lazy load pages for better performance
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/RegisterPage'))
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'))
const RegistrationOfferRecoveryPage = lazy(() => import('@/pages/RegistrationOfferRecoveryPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))
const SetupPage = lazy(() => import('@/pages/SetupPage'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const ServerPage = lazy(() => import('@/pages/ServerPage'))
const ServerListPage = lazy(() => import('@/pages/ServerListPage'))
const MinecraftNetworksPage = lazy(() => import('@/pages/MinecraftNetworksPage'))
const MinecraftNetworkDetailPage = lazy(() => import('@/pages/MinecraftNetworkDetailPage'))
const AccountOverviewPage = lazy(() => import('@/pages/AccountOverviewPage'))
const AccountProfilePage = lazy(() => import('@/pages/AccountPage'))
const ActivityPage = lazy(() => import('@/pages/ActivityPage'))
const BillingPage = lazy(() => import('@/pages/BillingPage'))
const DeployNewServicePage = lazy(() => import('@/pages/DeployNewServicePage'))
const ServiceDeployPage = lazy(() => import('@/pages/ServiceDeployPage'))
const ServiceDetailsPage = lazy(() => import('@/pages/ServiceDetailsPage'))
const SupportLandingPage = lazy(() => import('@/pages/SupportLandingPage'))
const SupportOverviewPage = lazy(() => import('@/pages/SupportOverviewPage'))
const SupportCreatePage = lazy(() => import('@/pages/SupportCreatePage'))
const SupportTicketPage = lazy(() => import('@/pages/SupportTicketPage'))

// Admin pages (root_admin only)
const AdminLandingPage = lazy(() => import('@/pages/AdminLandingPage'))
const AdminStripePricesPage = lazy(() => import('@/pages/AdminStripePricesPage'))
const AdminSubdomainsPage = lazy(() => import('@/pages/AdminSubdomainsPage'))
const AdminModerationPage = lazy(() => import('@/pages/AdminModerationPage'))
const AdminTasksPage = lazy(() => import('@/pages/AdminTasksPage'))
const AdminIntegrationsPage = lazy(() => import('@/pages/AdminIntegrationsPage'))
const AdminIntegrationDetailPage = lazy(() => import('@/pages/AdminIntegrationDetailPage'))
const AdminConfigurationRegistryPage = lazy(() => import('@/pages/AdminConfigurationRegistryPage'))
const AdminApiKeysPage = lazy(() => import('@/pages/AdminApiKeysPage'))
const AdminSettingsPage = lazy(() => import('@/pages/AdminSettingsPage'))
const AdminRegistrationFunnelPage = lazy(() => import('@/pages/AdminRegistrationFunnelPage'))
const AdminPackageBillingPage = lazy(() => import('@/pages/AdminPackageBillingPage'))
const AdminUsersPage = lazy(() => import('@/pages/AdminUsersPage'))
const AdminUserDetailPage = lazy(() => import('@/pages/AdminUserDetailPage'))
const AdminServersPage = lazy(() => import('@/pages/AdminServersPage'))
const AdminServerDetailPage = lazy(() => import('@/pages/AdminServerDetailPage'))
const AdminNodesPage = lazy(() => import('@/pages/AdminNodesPage'))
const AdminNodeDetailPage = lazy(() => import('@/pages/AdminNodeDetailPage'))
const AdminLocationsPage = lazy(() => import('@/pages/AdminLocationsPage'))
const AdminUpdatesPage = lazy(() => import('@/pages/AdminUpdatesPage'))
const AdminUpdateEditorPage = lazy(() => import('@/pages/AdminUpdateEditorPage'))
const AdminLocationDetailPage = lazy(() => import('@/pages/AdminLocationDetailPage'))
const AdminNestsPage = lazy(() => import('@/pages/AdminNestsPage'))
const AdminNestDetailPage = lazy(() => import('@/pages/AdminNestDetailPage'))
const AdminEggDetailPage = lazy(() => import('@/pages/AdminEggDetailPage'))
import AdminLayout from '@/components/AdminLayout'
import AdminRoute from '@/components/AdminRoute'
import SupportAdminRoute from '@/components/SupportAdminRoute'
const AdminSupportPage = lazy(() => import('@/pages/AdminSupportPage'))
const AdminSupportTicketPage = lazy(() => import('@/pages/AdminSupportTicketPage'))
const AdminSupportCustomersPage = lazy(() => import('@/pages/AdminSupportCustomersPage'))
const AdminSupportCustomerPage = lazy(() => import('@/pages/AdminSupportCustomerPage'))
const AdminSupportServersPage = lazy(() => import('@/pages/AdminSupportServersPage'))
const AdminSupportServerPage = lazy(() => import('@/pages/AdminSupportServerPage'))
const AdminOperationalLogsPage = lazy(() => import('@/pages/AdminOperationalLogsPage'))

// DNS pages
const DnsSubdomainsPage = lazy(() => import('@/pages/DnsSubdomainsPage'))

// Demo pages
const ModalDemoPage = import.meta.env.DEV ? lazy(() => import('@/pages/ModalDemoPage')) : null
const PrototypeIndexPage = import.meta.env.DEV ? lazy(() => import('@/prototypes/PrototypeIndexPage')) : null
const ConsoleV2PrototypePage = import.meta.env.DEV ? lazy(() => import('@/prototypes/consoleV2/ConsoleV2PrototypePage')) : null
const ServerLibraryPrototypePage = import.meta.env.DEV ? lazy(() => import('@/prototypes/serverLibrary/ServerLibraryPrototypePage')) : null
const PackageBillingPreviewPage = import.meta.env.DEV ? lazy(() => import('@/prototypes/PackageBillingPreviewPage')) : null
const CatalogCheckoutPreviewPage = import.meta.env.DEV ? lazy(() => import('@/prototypes/catalogCheckout/CatalogCheckoutPreviewPage')) : null

// Lazy load server sub-pages // rebuild
const ServerFilesPage = lazy(() => import('@/pages/server/ServerFilesPage'))
const ServerDatabasesPage = lazy(() => import('@/pages/server/ServerDatabasesPage'))
const ServerSchedulesPage = lazy(() => import('@/pages/server/ServerSchedulesPage'))
const ServerScheduleDetailPage = lazy(() => import('@/pages/server/ServerScheduleDetailPage'))
const SubusersPage = lazy(() => import('@/pages/server/SubusersPage'))
const DatabaseServerUsersPage = lazy(() => import('@/pages/databaseServer/DatabaseServerUsersPage'))
const DatabaseAssociationsPage = lazy(() => import('@/pages/databaseServer/DatabaseAssociationsPage'))
const ServerBackupsPage = lazy(() => import('@/pages/server/ServerBackupsPage'))
const ServerNetworkPage = lazy(() => import('@/pages/server/ServerNetworkPage'))
const ServerSubdomainsPage = lazy(() => import('@/pages/server/ServerSubdomainsPage'))
const ServerSettingsPage = lazy(() => import('@/pages/server/ServerSettingsPage'))
const ServerSftpPage = lazy(() => import('@/pages/server/ServerSftpPage'))
const ServerActivityPage = lazy(() => import('@/pages/server/ServerActivityPage'))
const ServerPerformancePage = lazy(() => import('@/pages/ServerPerformancePage'))
const ServerBillingPage = lazy(() => import('@/pages/ServerBillingPage'))
const ServerConsolePopupPage = lazy(() => import('@/pages/ServerConsolePopupPage'))
const ServerOverviewPage = lazy(() => import('@/pages/server/ServerOverviewPage'))
const ServerVersionSwitcherPage = lazy(() => import('@/pages/server/ServerVersionSwitcherPage'))
const ServerPluginManagerPage = lazy(() => import('@/pages/server/ServerPluginManagerPage'))
const ServerModsPage = lazy(() => import('@/pages/server/ServerModsPage'))
const ServerModpackManagerPage = lazy(() => import('@/pages/server/ServerModpackManagerPage'))
const ServerConfigurationPage = lazy(() => import('@/pages/server/ServerConfigurationPage'))
const ServerPlayersPage = lazy(() => import('@/pages/server/ServerPlayersPage'))
const ServerRustFrameworkPage = lazy(() => import('@/pages/server/ServerRustFrameworkPage'))
const ServerRustPluginManagerPage = lazy(() => import('@/pages/server/ServerRustPluginManagerPage'))
const ServerRustWipePage = lazy(() => import('@/pages/server/ServerRustWipePage'))
const ServerRustPlayersPage = lazy(() => import('@/pages/server/ServerRustPlayersPage'))
const ServerPalworldPage = lazy(() => import('@/pages/server/ServerPalworldPage'))
const MigrateServerPage = lazy(() => import('@/pages/server/MigrateServerPage'))
const ChangeTierPage = lazy(() => import('@/pages/server/ChangeTierPage'))
const ServerDangerZonePage = lazy(() => import('@/pages/server/ServerDangerZonePage'))
const ServerSteamPage = lazy(() => import('@/pages/server/ServerSteamPage'))
const ServerDiscordPage = lazy(() => import('@/pages/server/ServerDiscordPage'))

function App() {
  const { isAuthenticated, isLoading, initializeAuth, user, authUnavailable } = useAuthStore()
  const location = useLocation()
  const path = location.pathname
  const isAuthRoute = isAuthShellPath(path)

  useEffect(() => {
  // Defer initializeAuth on auth routes to avoid extra spinner/requests
  if (!isAuthRoute) {
    initializeAuth()
  } else {
    // Ensure auth store isn't left in loading state on auth pages
    useAuthStore.setState({ isLoading: false })
  }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!authUnavailable) return
    const retryWhenOnline = () => { void initializeAuth() }
    window.addEventListener('online', retryWhenOnline)
    return () => window.removeEventListener('online', retryWhenOnline)
  }, [authUnavailable, initializeAuth])

  if (isLoading && !isAuthRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (path === '/registration-recovery') {
    return <Suspense fallback={<LoadingSpinner size="lg" />}><RegistrationOfferRecoveryPage /></Suspense>
  }

  if (!isAuthenticated || !user) {
    return (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/password" element={<ForgotPasswordPage />} />
          <Route path="/auth/password/reset/:token" element={<ResetPasswordPage />} />
          <Route path="/setup/:token" element={<SetupPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    )
  }

  // Check if this is the console popup route (should render without Layout)
  const isConsolePopup = location.pathname.match(/^\/servers\/[^/]+\/console-popup$/)

  // Console popup - render without Layout for clean fullscreen experience
  if (isConsolePopup) {
    return (
      <>
        <RegistrationSessionStartupCoordinator />
        <Suspense fallback={
          <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }>
          <Routes>
            <Route path="/servers/:uuid/console-popup" element={<ServerConsolePopupPage />} />
          </Routes>
        </Suspense>
        <RequireEmailVerificationGate />
      </>
    )
  }

  if (user.role === 'sre') {
    const allowedSrePath = path.startsWith('/admin/support') || path === '/account/profile' || path === '/account/security'
    if (!allowedSrePath) {
      return <Navigate to={user.use_totp ? '/admin/support' : '/account/profile#profile-security'} replace />
    }
  }

  if (authUnavailable && !user && !isAuthRoute) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-5 dark:bg-[#090b0f]">
        <div className="server-panel server-panel-elevated max-w-md p-6 text-center sm:p-8">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-amber-400/20 bg-amber-500/10 text-amber-500">
            <SignalSlashIcon className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-xl font-bold text-gray-950 dark:text-white">Reconnecting to Metrly</h1>
          <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-400">
            Your sign-in is still saved. Metrly could not reach the server control service, so we kept your session and stopped before sending you back to login.
          </p>
          <button type="button" onClick={() => void initializeAuth()} className="server-action-primary mt-5 min-h-11 rounded-lg px-5 text-sm font-semibold sm:min-h-10">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <ServerCancellationProvider key={user.uuid || String(user.id)}>
    <Layout>
      {user.role !== 'sre' && <RegistrationSessionStartupCoordinator />}
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/servers" element={<ServerListPage />} />
          {PrototypeIndexPage && <Route path="/prototype" element={<PrototypeIndexPage />} />}
          {ConsoleV2PrototypePage && <Route path="/prototype/console-v2" element={<ConsoleV2PrototypePage />} />}
          {ServerLibraryPrototypePage && <Route path="/prototype/server-library" element={<ServerLibraryPrototypePage />} />}
          {PackageBillingPreviewPage && <Route path="/prototype/package-billing" element={<PackageBillingPreviewPage />} />}
          {CatalogCheckoutPreviewPage && <Route path="/prototype/catalog-checkout" element={<CatalogCheckoutPreviewPage />} />}
          <Route path="/game-servers/networks" element={<MinecraftNetworksPage />} />
          <Route path="/game-servers/networks/:networkId" element={<MinecraftNetworkDetailPage />} />
          <Route path="/compute/deploy" element={<DeployNewServicePage />} />
          <Route path="/compute/services/:slug" element={<ServiceDetailsPage />} />
          <Route path="/compute/deploy/service" element={<ServiceDeployPage />} />
          <Route path="/deployment-progress" element={<Navigate to="/servers" replace />} />
          <Route path="servers/:uuid" element={<ServerPage />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<ServerOverviewPage />} />
            <Route path="console" element={null} />
            <Route path="console-popup" element={null} />
            <Route path="files" element={<ServerFilesPage />} />
            <Route path="databases" element={<ServerDatabasesPage />} />
            <Route path="schedules" element={<ServerSchedulesPage />} />
            <Route path="schedules/:scheduleId" element={<ServerScheduleDetailPage />} />
            <Route path="database-users" element={<DatabaseServerUsersPage />} />
            <Route path="server-users" element={<SubusersPage />} />
            <Route path="associations" element={<DatabaseAssociationsPage />} />
            <Route path="backups" element={<ServerBackupsPage />} />
            <Route path="network" element={<ServerNetworkPage />} />
            <Route path="subdomains" element={<ServerSubdomainsPage />} />
            <Route path="steam" element={<ServerSteamPage />} />
            <Route path="discord" element={<ServerDiscordPage />} />
            <Route path="settings" element={<Navigate to="../game-settings" replace />} />
            <Route path="game-settings" element={<ServerSettingsPage />} />
            <Route path="sftp" element={<ServerSftpPage />} />
            <Route path="danger-zone" element={<ServerDangerZonePage />} />
            <Route path="settings/danger" element={<Navigate to="../danger-zone" replace />} />
            <Route path="activity" element={<ServerActivityPage />} />
            <Route path="performance" element={<ServerPerformancePage />} />
            <Route path="graphs" element={<Navigate to="../performance" replace />} />
            <Route path="billing" element={<ServerBillingPage />} />
            <Route path="version-switcher" element={<ServerVersionSwitcherPage />} />
            <Route path="plugins" element={<ServerPluginManagerPage />} />
            <Route path="mods" element={<ServerModsPage />} />
            <Route path="modpacks" element={<ServerModpackManagerPage />} />
            <Route path="configuration" element={<ServerConfigurationPage />} />
            <Route path="players" element={<ServerPlayersPage />} />
            <Route path="rust-framework" element={<ServerRustFrameworkPage />} />
            <Route path="rust-plugins" element={<ServerRustPluginManagerPage />} />
            <Route path="rust-wipe" element={<ServerRustWipePage />} />
            <Route path="rust-players" element={<ServerRustPlayersPage />} />
            <Route path="palworld-settings" element={<ServerPalworldPage view="settings" />} />
            <Route path="palworld-migration" element={<ServerPalworldPage view="migration" />} />
            <Route path="palworld" element={<Navigate to="../palworld-settings" replace />} />
            <Route path="migrate" element={<MigrateServerPage />} />
            <Route path="change-tier" element={<ChangeTierPage />} />
          </Route>
          <Route path="/account" element={<AccountOverviewPage />} />
          <Route path="/account/backups" element={<BackupVaultPage />} />
          <Route path="/account/profile" element={<AccountProfilePage />} />
          <Route path="/account/security" element={<Navigate to="/account/profile#profile-security" replace />} />
          <Route path="/account/activity" element={<ActivityPage />} />
          <Route path="/account/api-tokens" element={<Navigate to="/account/profile?tab=api-tokens" replace />} />
          <Route path="/account/ssh-keys" element={<Navigate to="/account/profile?tab=ssh-keys" replace />} />
          <Route path="/account/connected-services" element={<Navigate to="/account/profile#profile-connections" replace />} />
          <Route path="/billing-overview" element={<BillingPage />} />
          <Route path="/billing-history" element={<Navigate to="/billing-overview?tab=invoices" replace />} />
          <Route path="/billing-settings" element={<Navigate to="/billing-overview?tab=payment-details" replace />} />
          <Route path="/dns/subdomains" element={<DnsSubdomainsPage />} />
          <Route path="/support" element={<SupportLandingPage />} />
          <Route path="/support/cases" element={<SupportOverviewPage />} />
          <Route path="/support/new" element={<SupportCreatePage />} />
          <Route path="/support/tickets/:ticketId" element={<SupportTicketPage />} />
          <Route path="admin/support" element={<SupportAdminRoute><AdminLayout /></SupportAdminRoute>}>
            <Route index element={<AdminSupportPage />} />
            <Route path="tickets/:ticketId" element={<AdminSupportTicketPage />} />
            <Route path="customers" element={<AdminSupportCustomersPage />} />
            <Route path="customers/:customerUuid" element={<AdminSupportCustomerPage />} />
            <Route path="servers" element={<AdminSupportServersPage />} />
            <Route path="servers/:serverUuid" element={<AdminSupportServerPage />} />
            <Route path="operational-logs" element={<AdminOperationalLogsPage />} />
          </Route>
          <Route path="admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminLandingPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:uuid" element={<AdminUserDetailPage />} />
            <Route path="servers" element={<AdminServersPage />} />
            <Route path="servers/:uuid" element={<AdminServerDetailPage />} />
            <Route path="nodes" element={<AdminNodesPage />} />
            <Route path="nodes/:id" element={<AdminNodeDetailPage />} />
            <Route path="nodes/:id/:tab" element={<AdminNodeDetailPage />} />
            <Route path="locations" element={<AdminLocationsPage />} />
            <Route path="updates" element={<AdminUpdatesPage />} />
            <Route path="updates/:updateId" element={<AdminUpdateEditorPage />} />
            <Route path="locations/:id" element={<AdminLocationDetailPage />} />
            <Route path="nests" element={<AdminNestsPage />} />
            <Route path="nests/:nestId" element={<AdminNestDetailPage />} />
            <Route path="nests/:nestId/eggs/:eggId" element={<AdminEggDetailPage />} />
            <Route path="stripe" element={<AdminStripePricesPage />} />
            <Route path="package-billing" element={<AdminPackageBillingPage />} />
            <Route path="dns" element={<AdminSubdomainsPage />} />
            <Route path="moderation" element={<AdminModerationPage />} />
            <Route path="integrations" element={<AdminIntegrationsPage />} />
            <Route path="integrations/:integrationSlug" element={<AdminIntegrationDetailPage />} />
            <Route path="integrations/email/settings" element={<AdminIntegrationsPage mode="email" />} />
            <Route path="integrations/steam/settings" element={<AdminIntegrationsPage mode="steam" />} />
            <Route path="developer/configuration-registry" element={<AdminConfigurationRegistryPage />} />
            <Route path="api-keys" element={<AdminApiKeysPage />} />
            <Route path="tasks" element={<AdminTasksPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="analytics/registration-funnel" element={<AdminRegistrationFunnelPage />} />
          </Route>
          {ModalDemoPage && <Route path="/design/modals" element={<ModalDemoPage />} />}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <DevDiagnostics />
      <RequireEmailVerificationGate />
      <ForcePasswordChangeModal />
      <RequirePaymentMethodGate />
    </Layout>
    </ServerCancellationProvider>
  )
}

export default App
